import {
  buildLetterheadHeader,
  buildFooter,
  buildWatermarkBackground,
  buildPatientIdentificationBlock,
  buildProfessionalIdentificationBlock,
  buildProfessionalStampBlock,
  formatDate,
  DOC_STYLES,
} from './sharedBlocks';
import type { EmitCertificadoResult } from './types';

// Certificados NO llevan contenido clínico — EmitCertificadoResult no trae
// motivo_consulta/notas_terapeuta/plan_tratamiento ni ningún otro campo
// clínico en ninguna de sus ramas (ver RPC emitir_certificado en
// sql/migrations/ficha_clinica_v3_certificados_2026_08_20.sql sección 5).
// No hay riesgo de fuga de datos clínicos sensibles en ninguno de estos 4
// documentos por diseño del propio tipo, no solo por disciplina acá.

function fmt(v?: string | null): string {
  return formatDate(v ?? undefined);
}

function nombreCompleto(paciente: EmitCertificadoResult['paciente']): string {
  return `${paciente.nombre} ${paciente.apellido}`.trim();
}

function baseDocDefinition(
  result: EmitCertificadoResult,
  logoDataUri: string | null,
  titulo: string,
  cuerpo: any[]
): any {
  return {
    pageMargins: [40, 90, 40, 60],
    header: buildLetterheadHeader(logoDataUri),
    footer: buildFooter({ nombre: result.profesional.nombre, apellido: result.profesional.apellido }),
    background: buildWatermarkBackground(logoDataUri),
    defaultStyle: { font: 'Helvetica', fontSize: 9, lineHeight: 1.25 },
    styles: DOC_STYLES,
    content: [
      { text: titulo, style: 'title' },
      buildPatientIdentificationBlock(result.paciente),
      buildProfessionalIdentificationBlock(result.profesional),
      ...cuerpo,
      buildProfessionalStampBlock(result.profesional),
    ],
    info: {
      title: `${titulo} — ${nombreCompleto(result.paciente)}`,
      author: 'Conectemos Chile SPA',
    },
  };
}

export function buildAsistenciaCertDocDefinition(result: EmitCertificadoResult, logoDataUri: string | null): any {
  const destinatarioLinea = result.destinatario?.trim()
    ? `A quien corresponda: ${result.destinatario.trim()}`
    : 'A quien corresponda:';

  const rango =
    result.fecha_desde && result.fecha_hasta
      ? `, entre el ${fmt(result.fecha_desde)} y el ${fmt(result.fecha_hasta)}`
      : ' a la fecha';

  const cuerpo = [
    { text: destinatarioLinea, margin: [0, 10, 0, 10] },
    {
      text: `Certifico que ${nombreCompleto(result.paciente)}, RUT ${result.paciente.rut}, ha asistido a ${result.datos.sesiones_completadas} sesión(es) de atención psicológica en Conectemos Chile SPA${rango}.`,
      margin: [0, 0, 0, 10],
    },
  ];

  return baseDocDefinition(result, logoDataUri, 'Certificado de Asistencia', cuerpo);
}

export function buildTratamientoLaboralCertDocDefinition(result: EmitCertificadoResult, logoDataUri: string | null): any {
  const inicio = fmt(result.datos.primera_sesion ?? result.datos.primera_consulta_registrada);
  let cuerpoTexto: string;

  if (result.datos.paciente_activo === true) {
    cuerpoTexto = `Certifico que ${nombreCompleto(result.paciente)} se encuentra en proceso de atención psicológica en esta consulta, iniciado el ${inicio}.`;
  } else {
    // NUNCA "actualmente en proceso" cuando paciente_activo es false — sería
    // una afirmación falsa en un documento de uso laboral. Tiempo pasado,
    // con rango de fechas cerrado.
    const fin = fmt(result.datos.ultima_sesion);
    cuerpoTexto = `Certifico que ${nombreCompleto(result.paciente)} recibió atención psicológica en esta consulta entre el ${inicio} y el ${fin}.`;
  }

  const cuerpo = [{ text: cuerpoTexto, margin: [0, 10, 0, 10] }];

  return baseDocDefinition(result, logoDataUri, 'Certificado de Tratamiento', cuerpo);
}

export function buildLegalJudicialCertDocDefinition(result: EmitCertificadoResult, logoDataUri: string | null): any {
  const rango =
    result.fecha_desde && result.fecha_hasta
      ? `Período certificado: ${fmt(result.fecha_desde)} al ${fmt(result.fecha_hasta)}.`
      : 'Período certificado: a la fecha de emisión.';

  // Cuerpo legal exacto PENDIENTE de aprobación del usuario — placeholder
  // visible a propósito, nunca completar con texto inventado. NUNCA agregar
  // acá motivo_consulta/notas_terapeuta/plan_tratamiento ni ningún otro
  // campo clínico — EmitCertificadoResult no los trae de todas formas (ver
  // comentario arriba), pero queda explícito por si este builder se toca a
  // futuro.
  //
  // Decisión del usuario (2026-08-20): mientras no exista el contenido legal
  // aprobado, el documento debe advertir explícitamente que emitirlo tal
  // cual carece de validez legal — evita que la clínica lo use en la
  // práctica pensando que es un certificado formal completo.
  const cuerpo = [
    { text: `Sesiones registradas: ${result.datos.sesiones_completadas}.`, margin: [0, 10, 0, 4] },
    { text: rango, margin: [0, 0, 0, 10] },
    {
      text: 'AVISO — CONTENIDO LEGAL NO INTEGRADO',
      bold: true,
      color: '#B00020',
      fontSize: 11,
      margin: [0, 0, 0, 4],
    },
    {
      text:
        'Este documento todavía no incluye el texto legal formal requerido para tener validez ' +
        'ante tribunales u otras instancias legales. Contactá al equipo de desarrollo para ' +
        'integrar esa fase antes de emitir certificados con fines legales o judiciales. ' +
        'Mientras este aviso esté presente, el documento carece de validez legal y no debe ' +
        'entregarse al paciente ni a terceros para ese fin.',
      bold: true,
      color: '#B00020',
      fontSize: 10,
      margin: [0, 0, 0, 10],
    },
  ];

  return baseDocDefinition(result, logoDataUri, 'Certificado para Fines Legales', cuerpo);
}

export function buildTextoLibreCertDocDefinition(result: EmitCertificadoResult, logoDataUri: string | null): any {
  const cuerpo = [{ text: result.texto_libre?.trim() || 'Sin contenido.', margin: [0, 10, 0, 10] }];
  return baseDocDefinition(result, logoDataUri, 'Certificado', cuerpo);
}
